import { createSlice } from "@reduxjs/toolkit";
import { api } from './apiSlice'
import { optimisticCreate, optimisticDelete, optimisticPublishUnpublish } from "./optimisticUpdates/offersOptimisticUpdates";

export const createOfferImportFormData = (listingId, importFile) => {
    const formBody = new FormData()
    formBody.append('importFile', importFile)
    formBody.append('listingId', listingId)
    return formBody
}

export const offersApiSlice = api.injectEndpoints({
    endpoints: build => ({
        getOffersByPartnerListingId: build.query({
            query: partnerListingId => `/offers/listing/${partnerListingId}/all`,
            providesTags: ['Offer'],
            transformResponse: (response) => {
                const offers = response ?
                    response.map(offer => ({ ...offer, billingRules: { ...offer.billingRules, commissionRate: Number(Number(offer.billingRules.commissionRate).toFixed(2)) } })) : []
                return offers
            }
        }),
        getOffersByCodePoolId: build.query({
            query: codePoolId => `/offers/bycodepool/${codePoolId}/published`,
            providesTags: ['OffersByCodePool']
        }),
        getOfferById: build.query({
            query: offerId => `/offers/${offerId}`,
            providesTags: ['Offer'],
            transformResponse: (response) => {
                return response.billingRules ? { ...response, billingRules: { ...response.billingRules, commissionRate: Number(Number(response.billingRules.commissionRate).toFixed(2)) } } : response
            }
        }),
        createTestRedemptionSession: build.mutation({
            query: offerId => ({
                url: `/marketplace/offers/${offerId}/test-redemption-session`,
                method: 'POST',
                body: {},
            }),
        }),
        getBillingRulesForOffer: build.query({
            query: offer => `/resources/billing-rules/listing/${offer.listingId}/offer/${offer.offerId}`,
            providesTags: ['OfferBillingRules'],
            transformResponse: (response) => {
                return { ...response, commissionRate: Number(Number(response.commissionRate).toFixed(2)) }
            }

        }),
        createOffer: build.mutation({
            query: newOffer => ({
                url: 'offers',
                method: 'POST',
                body: newOffer,
                responseHandler: 'text',
            }),
            invalidatesTags: ['Offer', 'OffersByCodePool'],
        }),
        updateOffer: build.mutation({
            query: updatedOffer => ({
                url: `/offers/${updatedOffer.offerId}`,
                method: 'PUT',
                body: updatedOffer,
            }),
            invalidatesTags: ['Offer'],
        }),
        createSyndicateOffer: build.mutation({
            query: ({ offerId, listingId }) => ({
                url: '/offers/syndicate',
                method: 'POST',
                body: { offerId, listingId },
                responseHandler: 'text',
            }),
            invalidatesTags: ['SyndicateOffers', 'Offer'],
            transformResponse: (response) => {
                var parsedResponse = JSON.parse(response)
                return parsedResponse
            }
        }),
        updateBillingRulesforOffer: build.mutation({
            query: updatedBillingRules => ({
                url: `resources/billing-rules/listing/${updatedBillingRules.listingId}/offer/${updatedBillingRules.offerId}`,
                method: 'POST',
                body: updatedBillingRules.billingRules,
            }),
            invalidatesTags: ['OfferBillingRules'],
        }),
        updateSalesChannels: build.mutation({
            query: ({ offerId, channels }) => ({
                url: `/offers/${offerId}/channels`,
                method: 'PATCH',
                body: { channels },
            }),
            invalidatesTags: ['Offer'],
        }),
        updateInfo: build.mutation({
            query: ({ offerId, name, description, link, tags }) => ({
                url: `/offers/${offerId}/info`,
                method: 'PATCH',
                body: { name, description, link, tags },
            }),
            invalidatesTags: ['Offer'],
        }),
        publishOffer: build.mutation({
            query: offer => ({
                url: `/offers/${offer.offerId}/published-status`,
                method: 'PATCH',
                body: { published: true },
            }),

            // optimistic update to listing
            onQueryStarted: optimisticPublishUnpublish(true),
            invalidatesTags: ['Offer'],

        }),
        unpublishOffer: build.mutation({
            query: offer => ({
                url: `/offers/${offer.offerId}/published-status`,
                method: 'PATCH',
                body: { published: false },
            }),
            // optimistic update to listing
            onQueryStarted: optimisticPublishUnpublish(false),
            invalidatesTags: ['Offer'],

        }),
        deleteOffer: build.mutation({
            query: offer => ({
                url: `/offers/${offer.offerId}`,
                method: 'DELETE',
            }),
            // optimistic update to listing
            onQueryStarted: optimisticDelete(),
        }),
        deleteSyndicateOffer: build.mutation({
            query: syndicateOffer => ({
                url: `/offers/syndicate/${syndicateOffer.syndicateId}/${syndicateOffer.syndicateListingId}`,
                method: 'DELETE',
            }),
            // optimistic update to listing
            invalidatesTags: ['SyndicateOffers', 'Offer'],
        }),
        syncShopifyOffer: build.mutation({
            query: appId => ({
                url: `/offers/listing/shopifysync`,
                method: 'POST',
                body: { MyShopifyDomain: appId }
            })
        }),
        getOfferBillingRulesHistory: build.query({
            query: offer => `/resources/billing-rules/listing/${offer.listingId}/offer/${offer.offerId}/history`,
            transformResponse: (response) => {
                return { ...response, billingRules: response.billingRules.map(billingRules => ({ ...billingRules, commissionRate: Number(Number(billingRules.commissionRate).toFixed(2)) })) }
            }
        }),
        GetSyndicateOffersByPartnerListingId: build.query({
            query: partnerListingId => `/offers/syndicate/listing/${partnerListingId}`,
            providesTags: ['SyndicateOffers'],
            transformResponse: (response) => {
                const syndicateOffers = response ?
                    response.map(offer => ({ ...offer, billingRules: { ...offer.billingRules, commissionRate: Number(Number(offer.billingRules.commissionRate).toFixed(2)) } })) : []
                return syndicateOffers
            }
        }),
        importOffers: build.mutation({
            query: ({ id, body }) => ({
                url: 'offers/import-offers',
                method: 'POST',
                body: createOfferImportFormData(id, body.importFile),
                formData: true,
            }),
        }),
        getOfferImportById: build.query({
            query: importId => `/offers/offer-import/${importId}`,
        }),
    })
})

const initialState = [];
const offersSlice = createSlice({
    name: 'offersNew',
    initialState,
    reducers: {},
})

export const {
    useGetOffersByPartnerListingIdQuery,
    useGetOfferByIdQuery,
    useGetBillingRulesForOfferQuery,
    useLazyGetOfferBillingRulesHistoryQuery,
    useLazyGetBillingRulesForOfferQuery,
    useLazyGetOfferByIdQuery,
    useDeleteOfferMutation,
    usePublishOfferMutation,
    useUnpublishOfferMutation,
    useCreateOfferMutation,
    useCreateSyndicateOfferMutation,
    useUpdateBillingRulesforOfferMutation,
    useUpdateOfferMutation,
    useUpdateSalesChannelsMutation,
    useUpdateInfoMutation,
    useSyncShopifyOfferMutation,
    useGetOffersByCodePoolIdQuery,
    useGetSyndicateOffersByPartnerListingIdQuery,
    useDeleteSyndicateOfferMutation,
    useImportOffersMutation,
    useLazyGetOfferImportByIdQuery,
    useCreateTestRedemptionSessionMutation,
} = offersApiSlice;

export default offersSlice.reducer;
